import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { X } from "lucide-react";
import { QUESTIONS, computeResult, newOrders } from "@/components/candidati/sim/simData";
import { SimQuestion } from "@/components/candidati/sim/SimQuestion";
import { SimProcessing } from "@/components/candidati/sim/SimProcessing";
import { SimResult } from "@/components/candidati/sim/SimResult";
import { trapFocus } from "@/lib/trapFocus";

export const SimModal = ({ onClose }) => {
  const ref = useRef(null);
  const [answers, setAnswers] = useState([]);
  const [step, setStep] = useState(0);
  const [phase, setPhase] = useState("quiz");
  const [orders, setOrders] = useState(newOrders);
  const result = useMemo(() => (phase === "result" ? computeResult(answers) : null), [phase, answers]);

  useEffect(() => {
    const opener = document.activeElement;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    ref.current?.querySelector("button")?.focus();
    return () => {
      document.body.style.overflow = prev;
      opener?.focus?.();
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "Tab") trapFocus(e, ref.current);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    if (ref.current) ref.current.scrollTop = 0;
  }, [phase]);

  const onAnswer = useCallback((i) => {
    setAnswers((a) => {
      const next = [...a];
      next[step] = i;
      return next;
    });
    if (step + 1 === QUESTIONS.length) setPhase("processing");
    else setStep(step + 1);
  }, [step]);
  const onBack = useCallback(() => setStep((s) => Math.max(0, s - 1)), []);
  const onProcessed = useCallback(() => setPhase("result"), []);
  const restart = () => {
    setAnswers([]);
    setStep(0);
    setOrders(newOrders());
    setPhase("quiz");
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-3 sm:p-6">
      <div className="absolute inset-0 bg-black/70" style={{ backdropFilter: "blur(6px)" }} onClick={onClose} data-testid="sim-overlay" />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sim-title"
        className={`sim-window relative w-full ${phase === "result" ? "max-w-5xl" : "max-w-2xl"} max-h-[92vh] max-h-[92dvh] overflow-y-auto overscroll-contain rounded-2xl border border-[#1e2024] bg-[#07080a]/95`}
        data-testid="sim-modal"
      >
        <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-[#16171a] bg-[#07080a] px-4 h-11">
          {["#ff5f57", "#febc2e", "#28c840"].map((c) => (
            <span key={c} className="h-2.5 w-2.5 rounded-full opacity-70" style={{ background: c }} />
          ))}
          <span id="sim-title" className="ml-3 font-mono text-xs text-titanium truncate">prodigy_terminal — simulazione AI</span>
          <button type="button" onClick={onClose} className="ml-auto h-8 w-8 shrink-0 rounded-full flex items-center justify-center text-titanium hover:text-white" aria-label="Chiudi simulazione" data-testid="sim-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5 sm:p-8">
          <p className="mb-6 rounded-lg border border-[#1e2024] bg-white/[0.02] px-3 py-2 font-mono text-[11px] leading-relaxed text-[#80858d]" data-testid="sim-note">
            Simulazione dimostrativa: i risultati sono indicativi e non sostituiscono il test psico-attitudinale completo.
          </p>
          {phase === "quiz" && <SimQuestion key={step} index={step} order={orders[step]} selected={answers[step]} onAnswer={onAnswer} onBack={onBack} />}
          {phase === "processing" && <SimProcessing onDone={onProcessed} />}
          {phase === "result" && <SimResult result={result} onRestart={restart} />}
        </div>
      </div>
    </div>
  );
};
